import { memo, useEffect, useRef } from 'react';

/**
 * Emerald constellation field — a slow-drifting node/edge lattice that reads as
 * a neural graph without resorting to literal brain or circuit imagery.
 *
 * Isolated as a memoized leaf so its rAF loop never re-renders the page around
 * it. Honours prefers-reduced-motion and stands down on coarse pointers.
 */
export const MeshField = memo(({ height = 320 }: { height?: number }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarse = window.matchMedia('(pointer: coarse)').matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = canvas.offsetWidth;
    let h = height;

    const resize = () => {
      width = canvas.offsetWidth;
      h = height;
      canvas.width = width * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Desktop 800 cap is for full-bleed particle fields; a lattice reads best
    // far sparser, and mobile drops to a third.
    const count = coarse ? 26 : 64;
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      r: Math.random() * 1.4 + 0.7,
    }));

    const LINK = 132;

    const draw = () => {
      ctx.clearRect(0, 0, width, h);

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK) {
            const alpha = (1 - dist / LINK) * 0.22;
            ctx.strokeStyle = `rgba(16,185,129,${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const n of nodes) {
        ctx.fillStyle = 'rgba(52,211,153,0.7)';
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = () => {
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      draw();
    };

    let raf = 0;
    const loop = () => {
      step();
      raf = requestAnimationFrame(loop);
    };

    if (reduced) {
      draw(); // Single static frame.
    } else {
      raf = requestAnimationFrame(loop);
    }

    const onResize = () => {
      resize();
      draw();
    };
    window.addEventListener('resize', onResize);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [height]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height,
        // Fades the lattice out toward the copy so text contrast is untouched.
        maskImage:
          'radial-gradient(120% 100% at 78% 40%, #000 0%, transparent 72%)',
        WebkitMaskImage:
          'radial-gradient(120% 100% at 78% 40%, #000 0%, transparent 72%)',
        pointerEvents: 'none',
      }}
    />
  );
});

MeshField.displayName = 'MeshField';
